/**
 * ToolsAndAuditTab — iter87.81
 *
 * Consolidates one-time / destructive ops (Tools) at top with read-only
 * historical trails (Audit) at the bottom. Each surface lives in its own
 * <CollapsibleCard/> so the sidebar stays scannable.
 *
 * v1 wires the existing Orphan Recovery surface (only Tool shipped so
 * far) and reserves the Audit section for iter87.82+ once we wire the
 * compliance_actions viewer + tier-change audit + transfer history.
 */
import { Wrench, ScrollText, AlertTriangle, Shield, Inbox } from "lucide-react";
import { CollapsibleCard } from "./CollapsibleCard";
import OrphanRecoveryTab from "./OrphanRecoveryTab";
import FreeTierAuditCard from "./FreeTierAuditCard";

export default function ToolsAndAuditTab() {
    return (
        <div className="space-y-4" data-testid="tools-audit-tab-shell">
            <div className="flex items-center gap-2 text-xs text-zinc-500">
                <Wrench className="w-3 h-3 text-cyber" />
                <span>
                    iter87.81 — Tools (one-time, destructive) surface first;
                    Audit (read-only trails) below.
                </span>
            </div>

            {/* ============ TOOLS (top, write ops) ============ */}
            <CollapsibleCard
                icon={AlertTriangle}
                title="Orphan Recovery"
                subtitle="Leagues with no admin_email — assign, archive, or bulk-delete"
                testId="tools-audit-orphan-recovery-card"
                defaultOpen={true}
            >
                <OrphanRecoveryTab />
            </CollapsibleCard>

            <CollapsibleCard
                icon={Wrench}
                title="One-time Healers"
                subtitle="Data backfills, dedupers, migrations you run on-demand"
                testId="tools-audit-healers-card"
                defaultOpen={false}
            >
                <div className="text-xs text-zinc-500 p-4 border-2 border-dashed border-zinc-800 text-center">
                    No healers wired yet. Add scripts here as they're built —
                    each becomes a labeled button with a preview / dry-run
                    step + audit trail.
                </div>
            </CollapsibleCard>

            {/* ============ AUDIT ============ */}
            <CollapsibleCard
                icon={Inbox}
                title="Free-Tier Signups"
                subtitle="Free-tier rivalry requests — auto-approved via Turnstile, with per-row & bulk approve / reject / delete for stragglers"
                testId="tools-audit-free-tier-card"
                defaultOpen={false}
            >
                <FreeTierAuditCard />
            </CollapsibleCard>

            <CollapsibleCard
                icon={ScrollText}
                title="Compliance Actions"
                subtitle="Every tier change, transfer, delete — chronological"
                testId="tools-audit-compliance-card"
                defaultOpen={false}
            >
                <div className="text-xs text-zinc-500 p-4 border-2 border-dashed border-zinc-800 text-center">
                    Compliance Actions viewer wiring lands in iter87.82.
                    Backend `compliance_actions` collection is already the
                    source of truth — just needs the read-only UI.
                </div>
            </CollapsibleCard>

            <CollapsibleCard
                icon={Shield}
                title="Security Findings"
                subtitle="Read view of memory/security_posture_registry.md"
                testId="tools-audit-security-card"
                defaultOpen={false}
            >
                <div className="text-xs text-zinc-500 p-4 border-2 border-dashed border-zinc-800 text-center">
                    Security Posture registry viewer wires in iter87.82.
                </div>
            </CollapsibleCard>
        </div>
    );
}
